import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGiveawayTypes, useUpdateGiveaway } from '@/hooks/queries'
import { useAutoResize } from '@/hooks/use-auto-resize'
import { notifyError } from '@/lib/notify'
import { backgroundImage } from '@/lib/format'
import { cn } from '@/lib/cn'
import { giveawayPath, giveawayUrl, normalizeSlugInput, slugError, slugify } from '@/lib/slug'
import { isFirstEntries, type Giveaway, type GiveawayObjective } from '@/models/giveaway'
import type { Media } from '@/models/media'
import { MediaPickerOverlay } from './MediaPickerOverlay'
import { SlugField } from './giveaway/SlugField'
import { GalleryEditor, VideoField } from './media/GalleryEditor'
import { ToggleField } from './ui/ToggleField'
import { KeysStep } from './giveaway-new/KeysStep'
import { ObjectivesEditor, objectivesChanged, objectivesPayload } from './giveaway/ObjectivesEditor'
import { videoFieldValid, videoFieldValue } from '@/lib/video'

interface GiveawayEditProps {
  giveaway: Giveaway
  onClose: () => void
  /** True when the page the user is on is addressed by the slug being edited. */
  followSlugChange?: boolean
}

/**
 * Edit a live giveaway.
 *
 * Presentation is editable — name, link, description, terms and cover — and so
 * are the keys and entry options until the giveaway ends (to fix a typo'd key,
 * top up the pool, or change how people enter). The schedule and prize type
 * are fixed once entrants have acted on them — see `GiveawayEdit` in
 * api/endpoints.ts for why. The
 * fields that cannot change are shown, disabled, rather than hidden, so the
 * rule is visible instead of merely enforced.
 */
export function GiveawayEditForm({ giveaway, onClose, followSlugChange }: GiveawayEditProps) {
  const navigate = useNavigate()
  const update = useUpdateGiveaway()

  const [name, setName] = useState(giveaway.name ?? '')
  const [slug, setSlug] = useState(giveaway.slug ?? slugify(giveaway.name ?? ''))
  const [description, setDescription] = useState(giveaway.description ?? '')
  const [terms, setTerms] = useState(giveaway.terms ?? '')
  const [cover, setCover] = useState<Media | null>(giveaway.cover_media)
  const [video, setVideo] = useState(giveaway.video_url ?? '')
  const [gallery, setGallery] = useState<string[]>(giveaway.gallery)
  const [hideEntries, setHideEntries] = useState(giveaway.hide_entries)
  const [requireAll, setRequireAll] = useState(giveaway.require_all_objectives)
  const [keys, setKeys] = useState<string[]>(giveaway.prizes ?? [])
  const [editingKeys, setEditingKeys] = useState(false)
  const [objectives, setObjectives] = useState<GiveawayObjective[]>(giveaway.giveaway_objective)
  const [editingObjectives, setEditingObjectives] = useState(false)
  const { data: giveawayTypes } = useGiveawayTypes()
  const [selectingMedia, setSelectingMedia] = useState(false)

  // The API only returns keys to the owner; with none to show there is nothing
  // to edit. Once it has ended the keys have been handed out.
  const keysEditable = giveaway.prizes !== null && !giveaway.has_ended
  const keysChanged =
    keysEditable &&
    (keys.length !== giveaway.prizes!.length || keys.some((key, i) => key !== giveaway.prizes![i]))

  // Once it has ended the draw is done; changing how people enter means nothing.
  const objectivesEditable = !giveaway.has_ended
  const objectivesDirty =
    objectivesEditable && objectivesChanged(giveaway.giveaway_objective, objectives)

  const descriptionRef = useRef<HTMLTextAreaElement>(null)
  useAutoResize(descriptionRef, description)

  const slugProblem = slugError(slug)
  const coverSrc = cover?.src ?? giveaway.display_cover.src

  const save = async () => {
    if (name.trim().length === 0) {
      notifyError('Your giveaway needs a name.')
      return
    }
    if (slugProblem) {
      notifyError(slugProblem)
      return
    }
    if (!videoFieldValid(video)) {
      notifyError('The featured video needs a YouTube or Vimeo link, or leave it empty.')
      return
    }
    if (keysChanged && keys.length === 0) {
      notifyError('Your giveaway needs at least one key.')
      return
    }
    if (giveaway.id === null) return

    let updated
    try {
      updated = await update.mutateAsync({
        id: giveaway.id,
        name: name.trim(),
        slug,
        description: description.trim() || null,
        terms: terms.trim() || null,
        cover_media_id: cover?.id ?? null,
        video_url: videoFieldValue(video),
        gallery,
        hide_entries: hideEntries,
        require_all_objectives: requireAll,
        ...(keysChanged ? { prizes: keys } : {}),
        ...(objectivesDirty ? { giveaway_objective: objectivesPayload(objectives) } : {}),
      })
    } catch {
      // The failure is already shown as a toast; keep the form open to fix it.
      return
    }

    onClose()

    // The shareable address moved; take the reader with it.
    if (followSlugChange && updated.slug && updated.slug !== giveaway.slug) {
      navigate(giveawayPath(updated.slug), { replace: true })
    }
  }

  return (
    <div className="overlay-wrapper">
      {selectingMedia ? (
        <MediaPickerOverlay
          onClose={() => setSelectingMedia(false)}
          onSelect={(media) => {
            setCover(media)
            setSelectingMedia(false)
          }}
        />
      ) : null}

      {editingObjectives ? (
        <div className="content">
          <div className="section-title">Edit Entry Options</div>
          <ObjectivesEditor
            objectives={objectives}
            firstEntries={isFirstEntries(giveaway, giveawayTypes)}
            onBack={() => setEditingObjectives(false)}
            onSave={(next) => {
              setObjectives(next)
              setEditingObjectives(false)
            }}
          />
        </div>
      ) : editingKeys ? (
        <div className="content">
          <div className="section-title">Edit Keys</div>
          <KeysStep
            keys={keys}
            onBack={() => setEditingKeys(false)}
            onSave={(next) => {
              setKeys(next)
              setEditingKeys(false)
            }}
          />
        </div>
      ) : (
        <div className="content">
          <div className="section-title">Edit Giveaway</div>

          <div className="field-row" style={{ marginTop: 24 }}>
            <div className="field">
              <div className="field-name">Name</div>
              <div className="field-box">
                <input
                  className="field-box-input"
                  aria-label="Giveaway name"
                  value={name}
                  onChange={(event) => {
                    const next = event.target.value
                    setName(next)
                    // Keep the link in step with the name until it has been
                    // edited by hand, so renaming an event does not silently
                    // leave a stale link behind.
                    if (slug === slugify(name)) setSlug(slugify(next))
                  }}
                />
              </div>
            </div>
          </div>

          <SlugField
            slug={slug}
            onChange={(value) => setSlug(normalizeSlugInput(value))}
            onReset={() => setSlug(slugify(name))}
            error={slugProblem}
            previewUrl={giveawayUrl(slug)}
          />

          <div className="field-row">
            <div className="field">
              <div className="field-name">Description</div>
              <textarea
                ref={descriptionRef}
                className="description-field"
                placeholder="Type description here..."
                aria-label="Giveaway description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
              <div className="field-hint">
                Web addresses become links. To name one, write [text](https://…).
              </div>
            </div>
          </div>

          <VideoField
            label="Featured video (optional)"
            value={video}
            onChange={setVideo}
            hint={
              giveaway.game_info?.video_url
                ? "Leave empty to show the game's video."
                : 'A YouTube or Vimeo link. Optional.'
            }
          />

          <GalleryEditor
            items={gallery}
            onChange={setGallery}
            hint={
              giveaway.game_info && giveaway.game_info.gallery.length > 0
                ? "Leave empty to show the game's gallery."
                : undefined
            }
          />

          <ToggleField
            label="Hide the number of entries"
            checked={hideEntries}
            onChange={setHideEntries}
            hint="Only you will see how many entries it has."
          />

          <div className="field-row">
            <div className="field">
              <div className="field-name">Terms &amp; conditions</div>
              <textarea
                className="description-field"
                style={{ minHeight: 90 }}
                placeholder="Optional"
                aria-label="Terms and conditions"
                value={terms}
                onChange={(event) => setTerms(event.target.value)}
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <div className="field-name">Cover image</div>
              {/* The art shown is whatever the giveaway currently renders with —
                an explicit upload, or the game's own header. Either way it is
                a real preview, so the field is `filled` and the label rides
                over it rather than replacing it. */}
              <button
                type="button"
                className={cn('media-field background-cover cover-picker', coverSrc && 'filled')}
                style={backgroundImage(coverSrc)}
                onClick={() => setSelectingMedia(true)}
              >
                <div className="media-field-content">
                  <div className="content-row upper button cover-picker-label">
                    {coverSrc ? 'Change cover image' : 'Choose cover image'}
                  </div>
                </div>
              </button>
            </div>
          </div>

          {keysEditable ? (
            <div className="field-row">
              <div className="field">
                <div className="field-name">Keys</div>
                <button
                  type="button"
                  className="field-box button"
                  onClick={() => setEditingKeys(true)}
                >
                  <div className="field-box-prepend">
                    <img src="/icons/stack.png" width={20} height={20} alt="" />
                  </div>
                  <div className="field-box-content">
                    {keys.length} key{keys.length === 1 ? '' : 's'}
                    {keysChanged ? ' (unsaved)' : ''}
                  </div>
                  <div className="field-box-append" />
                </button>
                <div className="field-hint">
                  Add, remove or fix keys. Keys already won are not affected.
                </div>
              </div>
            </div>
          ) : null}

          {objectivesEditable ? (
            <div className="field-row">
              <div className="field">
                <div className="field-name">Entry options</div>
                <button
                  type="button"
                  className="field-box button"
                  onClick={() => setEditingObjectives(true)}
                >
                  <div className="field-box-prepend">
                    <img src="/icons/add_objective.png" width={20} height={20} alt="" />
                  </div>
                  <div className="field-box-content">
                    {objectives.length} option{objectives.length === 1 ? '' : 's'}
                    {objectivesDirty ? ' (unsaved)' : ''}
                  </div>
                  <div className="field-box-append" />
                </button>
                <div className="field-hint">
                  Add, remove or revalue entry options. People keep entries they already have.
                </div>
              </div>
            </div>
          ) : null}

          {objectivesEditable && isFirstEntries(giveaway, giveawayTypes) ? (
            <ToggleField
              label="Require every entry option to claim a key"
              checked={requireAll}
              onChange={setRequireAll}
              hint="Off: completing any one option claims a key. On: people have to complete every option first. People who already have a key keep it."
            />
          ) : null}

          <LockedFields
            giveaway={giveaway}
            showPrizes={!keysEditable}
            showObjectives={!objectivesEditable}
          />

          <div className="footer">
            <div className="cta-row">
              <button type="button" className="cta" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="cta next"
                onClick={save}
                disabled={update.isPending || !!slugProblem}
              >
                {update.isPending ? <span className="spinner-inline" aria-hidden="true" /> : null}
                {update.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * The terms entrants already accepted, shown read-only. Hiding them would make
 * the restriction feel like a missing feature rather than a deliberate one.
 */
function LockedFields({
  giveaway,
  showPrizes,
  showObjectives,
}: {
  giveaway: Giveaway
  showPrizes: boolean
  showObjectives: boolean
}) {
  const formatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' })

  return (
    <div className="locked-fields">
      <div className="locked-fields-note">
        Locked once entries open — changing these would move the goalposts for people who have
        already entered.
      </div>

      <div className="field-row">
        <div className="field half">
          <div className="field-name">Starts</div>
          <div className="field-box is-locked">
            <div className="field-box-content small">{formatter.format(giveaway.scheduled_at)}</div>
          </div>
        </div>
        <div className="field half">
          <div className="field-name">Ends</div>
          <div className="field-box is-locked">
            <div className="field-box-content small">{formatter.format(giveaway.finishes_at)}</div>
          </div>
        </div>
      </div>

      <div className="field-row">
        {showPrizes ? (
          <div className="field half">
            <div className="field-name">Prizes</div>
            <div className="field-box is-locked">
              <div className="field-box-content small">{giveaway.prizes?.length ?? 0} keys</div>
            </div>
          </div>
        ) : null}
        {showObjectives ? (
          <div className="field half">
            <div className="field-name">Entry options</div>
            <div className="field-box is-locked">
              <div className="field-box-content small">
                {giveaway.giveaway_objective.length} option
                {giveaway.giveaway_objective.length === 1 ? '' : 's'}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
